import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Equipment from "../types/equipment";
import SearchParams, { defaultParams } from "../types/searchParams";
import SearchResults from "../types/searchResults";
import http from "../api/http";
import getSearchQueryString from "../helpers/getSearchQueryString";

interface EquipmentState {
  equipment: Equipment;
  equipmentFilter: SearchParams;
  equipmentSearchResults?: SearchResults;
  status: "idle" | "loading" | "failed";
}

const initialState: EquipmentState = {
  equipment: { id: 0, name: "", value: 0 },
  equipmentFilter: defaultParams,
  status: "idle",
};

export const getEquipmentId = createAsyncThunk<Equipment, number>(
  "equipment/get/id",
  async (id: number) => {
    const response = await http.get<Equipment>(`/equipment/${id}`);
    return response.data;
  }
);

export const postEquipment = createAsyncThunk<Equipment, Equipment>(
  "equipment/post",
  async (equipment: Equipment) => {
    const response = await http.post<Equipment>(`/equipment`, equipment, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const editEquipment = createAsyncThunk<Equipment, Equipment>(
  "equipment/edit",
  async (equipment: Equipment) => {
    const response = await http.put<Equipment>(
      `/equipment/${equipment.id}`,
      equipment,
      {
        params: {
          successToast: true,
        },
      }
    );
    return response.data;
  }
);

export const deleteEquipment = createAsyncThunk<Equipment, number>(
  "equipment/delete",
  async (id: number) => {
    const response = await http.delete<Equipment>(`/equipment/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const searchEquipment = createAsyncThunk<SearchResults, SearchParams>(
  "equipment/search",
  async (search: SearchParams) => {
    let qs = getSearchQueryString(search);
    const response = await http.get<SearchResults>(`/equipment/search?${qs}`);
    return response.data;
  }
);

const equipmentSlice = createSlice({
  name: "equipment",
  initialState,
  reducers: {
    resetEquipment(state) {
      state.equipment = { ...initialState.equipment };
    },
    setEquipmentFilter(state, action) {
      state.equipmentFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEquipmentId.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getEquipmentId.fulfilled, (state, action) => {
        state.status = "idle";
        state.equipment = action.payload;
      })
      .addCase(getEquipmentId.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(postEquipment.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postEquipment.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(postEquipment.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editEquipment.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editEquipment.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(editEquipment.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteEquipment.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteEquipment.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(deleteEquipment.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(searchEquipment.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchEquipment.fulfilled, (state, action) => {
        state.status = "idle";
        state.equipmentSearchResults = action.payload;
      })
      .addCase(searchEquipment.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export default equipmentSlice.reducer;
export const { resetEquipment, setEquipmentFilter } = equipmentSlice.actions;
